import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Button, Modal } from 'antd';
import { InfoCircleOutlined } from "@ant-design/icons";

import { colors } from '../../utils/variables.styles';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';
import RangeEdit from "../../components/RangeEdit/RangeEdit";

import selector from './doorsEditor.selector';

import { infoShape } from '../../types/info.types';
import { availableType } from '../../types/available.types';

import {
  selectGates,
  setDoors,
  setSelectedLocal,
  setUiOpenSection
} from '../Construction/construction.reducer';

import withTranslation from "../../components/WithTranslation/WithTranslation";
import { selectDoors, selectWidth, selectLength, selectFirstHeight, selectSecondHeight } from "../Construction/construction.reducer";

const Container = styled.div`
  background: ${colors.whiteer};
  padding: 10px 24px;
  margin-left: -27px;
`;

const Spacer = styled.div`
  padding: 10px 24px;
  font-weight: 600;
  max-width: 280px;
`;

const ImgWrapper = styled.img`
  max-width: 100%;
  display: block;
  margin: 15px auto;
`;

const ButtonWrapper = styled.div`
  padding-top: 5px;
`;

const DoorsEditor = ({ collapsed, t }) => {
  const dispatch = useDispatch();

  // 🔹 wszystko z selektora
  const {
    loaded,

    door_back_count_available,
    door_back_count_info,
    door_back_count_selected,
    door_back_size_available,
    door_back_size_info,
    door_back_size_selected,
    door_back_height_available,
    door_back_height_info,
    door_back_height_selected,
    door_back_opening_available,
    door_back_opening_info,
    door_back_opening_selected,

    door_front_count_available,
    door_front_count_info,
    door_front_count_selected,
    door_front_size_available,
    door_front_size_info,
    door_front_size_selected,
    door_front_height_available,
    door_front_height_info,
    door_front_height_selected,
    door_front_opening_available,
    door_front_opening_info,
    door_front_opening_selected,

    door_left_count_available,
    door_left_count_info,
    door_left_count_selected,
    door_left_size_available,
    door_left_size_info,
    door_left_size_selected,
    door_left_height_available,
    door_left_height_info,
    door_left_height_selected,
    door_left_opening_available,
    door_left_opening_info,
    door_left_opening_selected,

    door_right_count_available,
    door_right_count_info,
    door_right_count_selected,
    door_right_size_available,
    door_right_size_info,
    door_right_size_selected,
    door_right_height_available,
    door_right_height_info,
    door_right_height_selected,
    door_right_opening_available,
    door_right_opening_info,
    door_right_opening_selected,
  } = useSelector(selector);

  const width = useSelector(selectWidth);
  const length = useSelector(selectLength);
  const doors = useSelector(selectDoors);
  const firstHeight = useSelector(selectFirstHeight);
  const secondHeight = useSelector(selectSecondHeight);
  const gates = useSelector(selectGates);

  // ✅ otwieranie/zamykanie tej sekcji z Redux (klucz: 'doors')
  const isDoorsOpen = useSelector(
    (state) => state?.Construction?.openSections?.doors ?? false
  );

  const doorsWidth = 100;

  // ✅ kompatybilne bramy do przypisania drzwi
  const getCompatibleGatesForDoors = (g) => {
    const list = g?.gates ?? [];
    return list.filter(
      (x) => x && (x.type === 'double_leaf_gate' || x.type === 'swing_gate')
    );
  };

  const compatibleGates = React.useMemo(
    () => getCompatibleGatesForDoors(gates),
    [gates]
  );

  const getGateLabel = (gate, index) => {
    return (
      gate?.name ||
      gate?.title ||
      gate?.label ||
      gate?.code ||
      `${gate?.type || 'brama'} #${index + 1}`
    );
  };

  const findGateById = React.useCallback(
    (gateId) => (compatibleGates || []).find((g) => String(g?.id) === String(gateId)),
    [compatibleGates]
  );

  const openModal = () => {
    Modal.info({
      title: door_front_size_info?.info_title,
      content: (
        <>
          {door_front_size_info?.img_one && <ImgWrapper src={door_front_size_info.img_one} />}
          {door_front_size_info?.img_two && <ImgWrapper src={door_front_size_info.img_two} />}
          {door_front_size_info?.info_content}
          {door_front_size_info?.img_three && <ImgWrapper src={door_front_size_info.img_three} />}
          {door_front_size_info?.img_four && <ImgWrapper src={door_front_size_info.img_four} />}
        </>
      )
    });
  };

  // ==========================
  // ✅ RULE: secondHeight < 200 => tylko front może mieć drzwi >= 200
  // ==========================
  const parseHeightValue = (v) => {
    if (v == null) return NaN;
    const n = parseFloat(String(v).replace(',', '.').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? n : NaN;
  };

  const getHeightValueById = (available, id) => {
    const found = (available || []).find((x) => String(x.id) === String(id));
    return found ? parseHeightValue(found.value) : NaN;
  };

  const filterHeightAvailableForWall = (wall, heightAvailable) => {
    if (secondHeight < 200 && wall !== 'front_wall') {
      return (heightAvailable || []).filter((el) => parseHeightValue(el.value) < 200);
    }
    return heightAvailable || [];
  };

  const pickFallbackHeightId = (wall, heightAvailable) => {
    const filtered = filterHeightAvailableForWall(wall, heightAvailable);
    return filtered?.[0]?.id ?? null;
  };

  // ==========================
  // ✅ GATE RULES:
  // 1) Jedna strona na bramę: jeśli left zajęte -> nie można dać kolejnych drzwi left (i analogicznie right)
  // 2) Drzwi muszą być niższe niż brama (brama min 1 cm wyższa): doorHeight < gate.height
  // ==========================
  const getFrontDoors = React.useCallback(() => {
    return (doors?.front_wall?.doors || []);
  }, [doors?.front_wall?.doors]);

  const getOccupiedSidesForGate = React.useCallback(
    (gateId, excludeIndex = -1) => {
      const occupied = new Set();
      getFrontDoors().forEach((d, i) => {
        if (i === excludeIndex) return;
        if (!d?.gateId) return;
        if (String(d.gateId) !== String(gateId)) return;
        if (d.gateSide === 'left' || d.gateSide === 'right') occupied.add(d.gateSide);
      });
      return occupied;
    },
    [getFrontDoors]
  );

  const pickFreeSideForGate = React.useCallback(
    (gateId, excludeIndex = -1) => {
      const occ = getOccupiedSidesForGate(gateId, excludeIndex);
      if (!occ.has('left')) return 'left';
      if (!occ.has('right')) return 'right';
      return null;
    },
    [getOccupiedSidesForGate]
  );

  const isDoorHeightAllowedForGate = React.useCallback(
    (gateId, doorHeightId, heightAvailableForFront) => {
      const gate = findGateById(gateId);
      const gateH = Number(gate?.height);
      if (!Number.isFinite(gateH)) return true; // jeśli nie mamy danych, nie blokuj

      const dh = getHeightValueById(heightAvailableForFront, doorHeightId);
      if (!Number.isFinite(dh)) return true;

      // brama musi być co najmniej 1 cm wyższa -> doorHeight < gateHeight
      return dh < gateH;
    },
    [findGateById]
  );

  const showGateHeightWarning = (gateId) => {
    const gate = findGateById(gateId);
    const gateH = Number(gate?.height);
    Modal.warning({
      title: 'Za wysokie drzwi dla tej bramy',
      content: `Dla tej bramy wysokość drzwi musi być mniejsza niż wysokość bramy. Brama ma ${Number.isFinite(gateH) ? gateH : '—'} cm, więc drzwi muszą mieć maksymalnie ${Number.isFinite(gateH) ? gateH - 1 : '—'} cm.`,
    });
  };

  const showGateSideBusyWarning = () => {
    Modal.warning({
      title: 'Strona bramy jest zajęta',
      content: 'Ta strona bramy jest już zajęta przez inne drzwi. Wybierz drugą stronę.',
    });
  };

  const onDoorPositionChange = (wall, index, value) => {
    const updatedDoorsArray = (doors[wall]?.doors || []).map((d, i) =>
      i === index ? { ...d, position: value } : d
    );
    dispatch(setDoors(wall, updatedDoorsArray));
  };

  // ✅ zapisuj height globalnie per ściana do obiektów drzwi
  const onDoorHeightChangeWall = (wall, heightId) => {
    const updatedDoorsArray = (doors[wall]?.doors || []).map((d) => ({
      ...d,
      height: heightId,
    }));
    dispatch(setDoors(wall, updatedDoorsArray));
  };

  // ✅ zapisuj opening globalnie per ściana do obiektów drzwi
  const onDoorOpeningChangeWall = (wall, openingId) => {
    const updatedDoorsArray = (doors[wall]?.doors || []).map((d) => ({
      ...d,
      opening: openingId,
    }));
    dispatch(setDoors(wall, updatedDoorsArray));
  };

  // ✅ przypisanie drzwi (front) do bramy i strony
  const onDoorAssignGateChange = (wall, index, gateIdOrZero) => {
    const gateId = gateIdOrZero === 0 ? null : gateIdOrZero;

    // jeśli odpinamy
    if (!gateId) {
      const updatedDoorsArray = (doors[wall]?.doors || []).map((d, i) => {
        if (i !== index) return d;
        return { ...d, gateId: null, gateSide: null };
      });
      dispatch(setDoors(wall, updatedDoorsArray));
      return;
    }

    // 1) blokada: obie strony zajęte
    const freeSide = pickFreeSideForGate(gateId, index);
    if (!freeSide) {
      Modal.warning({
        title: 'Brak wolnego miejsca w bramie',
        content: 'Ta brama ma już zajęte obie strony drzwi. Wybierz inną bramę.',
      });
      return;
    }

    // 2) blokada: wysokość drzwi musi być < wysokości bramy
    const currentDoor = (doors[wall]?.doors || [])[index];
    const currentHeightId = currentDoor?.height ?? door_front_height_selected; // per-door, albo wall selected
    if (!isDoorHeightAllowedForGate(gateId, currentHeightId, door_front_height_available)) {
      showGateHeightWarning(gateId);
      return;
    }

    // ustawiamy gateId + jeśli gateSide pusta lub zajęta -> ustawiamy wolną
    const updatedDoorsArray = (doors[wall]?.doors || []).map((d, i) => {
      if (i !== index) return d;

      const desiredSide = (d.gateSide === 'left' || d.gateSide === 'right') ? d.gateSide : null;
      const occupied = getOccupiedSidesForGate(gateId, index);

      let nextSide = desiredSide;
      if (!nextSide || occupied.has(nextSide)) nextSide = freeSide;

      return { ...d, gateId, gateSide: nextSide };
    });

    dispatch(setDoors(wall, updatedDoorsArray));
  };

  const onDoorAssignGateSideChange = (wall, index, side) => {
    const currentDoor = (doors[wall]?.doors || [])[index];
    const gateId = currentDoor?.gateId;

    if (!gateId) return;

    const occupied = getOccupiedSidesForGate(gateId, index);
    if (occupied.has(side)) {
      showGateSideBusyWarning();
      return;
    }

    const updatedDoorsArray = (doors[wall]?.doors || []).map((d, i) =>
      i === index ? { ...d, gateSide: side } : d
    );
    dispatch(setDoors(wall, updatedDoorsArray));
  };

  // ==========================
  // ✅ REPAIR: secondHeight < 200 => back/left/right nie mogą mieć >=200
  // ==========================
  React.useEffect(() => {
    if (secondHeight >= 200) return;

    const rules = [
      { wall: 'back_wall', selectedId: door_back_height_selected, available: door_back_height_available, key: 'door_back_height' },
      { wall: 'left_wall', selectedId: door_left_height_selected, available: door_left_height_available, key: 'door_left_height' },
      { wall: 'right_wall', selectedId: door_right_height_selected, available: door_right_height_available, key: 'door_right_height' },
    ];

    rules.forEach(({ wall, selectedId, available, key }) => {
      const currentVal = getHeightValueById(available, selectedId);

      if (Number.isFinite(currentVal) && currentVal >= 200) {
        const fallbackId = pickFallbackHeightId(wall, available);
        dispatch(setSelectedLocal(key, fallbackId));
        onDoorHeightChangeWall(wall, fallbackId);
      }
    });
  }, [
    secondHeight,
    door_back_height_selected,
    door_left_height_selected,
    door_right_height_selected,
    door_back_height_available,
    door_left_height_available,
    door_right_height_available,
    dispatch
  ]);

  // ✅ count: budujemy drzwi + ustawiamy selected LOKALNIE (bez natychmiastowego requesta)
  const handleDoorCountChange = (
    wall,
    paramKey,
    count,
    defaultHeight,
    defaultOpening
  ) => {
    let doorsCount = 0;
    if (count === 15) doorsCount = 1;
    if (count === 16) doorsCount = 2;

    if (wall === 'front_wall' && count === 10) doorsCount = 2;
    if (wall === 'front_wall' && count === 9) doorsCount = 1;
    if (wall === 'front_wall' && count === 8) doorsCount = 0;

    const updatedDoors = Array.from({ length: doorsCount }, (_, index) => {
      const existingDoor = doors[wall]?.doors?.[index];
      return existingDoor || {
        id: index,
        position: 20,
        size: 100,
        height: defaultHeight ?? null,
        opening: defaultOpening ?? null,
        gateId: null,
        gateSide: null, // 'left' | 'right'
      };
    });

    dispatch(setDoors(wall, updatedDoors));
    dispatch(setSelectedLocal(paramKey, count));
  };

  const renderWallControls = (
    wall,
    countSelected,
    countAvailable,
    countInfo,
    sizeSelected,
    sizeAvailable,
    sizeInfo,
    heightSelected,
    heightAvailable,
    heightInfo,
    openingSelected,
    openingAvailable,
    openingInfo,
    countKey,
    sizeKey,
    heightKey,
    openingKey,
    max
  ) => {
    const hasDoors = (doors[wall]?.doors?.length || 0) > 0;
    const isFront = wall === 'front_wall';

    const gatesOptions = [
      { id: 0, value: 'Nie przypisuj' },
      ...compatibleGates.map((g, idx) => ({
        id: g.id,
        value: getGateLabel(g, idx),
      })),
    ];

    // 👉 UWAGA: sideOptions liczymy PER-DOOR (zajętość)
    const getSideOptionsForDoor = (gateId, doorIndex, currentSide) => {
      const occ = getOccupiedSidesForGate(gateId, doorIndex);
      const options = [];
      // pokaż wolne + aktualnie wybraną (żeby nie zniknęła)
      if (!occ.has('left') || currentSide === 'left') options.push({ id: 'left', value: 'Z lewej strony' });
      if (!occ.has('right') || currentSide === 'right') options.push({ id: 'right', value: 'Z prawej strony' });
      return options;
    };

    // 👉 wysokości na front: jeśli jakiekolwiek drzwi przypięte do bram, to nie pozwalamy wybrać wysokości >= minHeightBram
    const getFrontGateMinHeight = () => {
      const frontDoors = getFrontDoors();
      const assigned = frontDoors.filter((d) => d?.gateId);
      if (assigned.length === 0) return null;

      let minH = Infinity;
      assigned.forEach((d) => {
        const g = findGateById(d.gateId);
        const h = Number(g?.height);
        if (Number.isFinite(h)) minH = Math.min(minH, h);
      });

      return Number.isFinite(minH) && minH !== Infinity ? minH : null;
    };

    const frontGateMinHeight = isFront ? getFrontGateMinHeight() : null;

    const filterHeightAvailableWithGateRule = (wallKey, list) => {
      // najpierw reguła secondHeight
      const filteredBase = filterHeightAvailableForWall(wallKey, list);

      // potem reguła bramy (tylko front): doorHeight < minGateHeight (jeśli są przypięte drzwi)
      if (wallKey === 'front_wall' && Number.isFinite(frontGateMinHeight) && frontGateMinHeight != null) {
        return (filteredBase || []).filter((el) => parseHeightValue(el.value) < frontGateMinHeight);
      }

      return filteredBase || [];
    };

    return (
      <>
        <SelectEdit
          selected={countSelected}
          loaded={loaded}
          title={countInfo.name}
          onChange={(count) =>
            handleDoorCountChange(
              wall,
              countKey,
              count,
              heightSelected,
              openingSelected
            )
          }
          options={countAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
          nested
        />

        {hasDoors && (
          <>
            <SelectEdit
              selected={sizeSelected}
              loaded={loaded}
              title={sizeInfo.name}
              onChange={(id) => dispatch(setSelectedLocal(sizeKey, id))}
              options={sizeAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
              nested
            />

            <SelectEdit
              selected={heightSelected}
              loaded={loaded}
              title={heightInfo.name}
              onChange={(id) => {
                const selectedVal = getHeightValueById(heightAvailable, id);

                // reguła secondHeight
                if (secondHeight < 200 && wall !== 'front_wall' && Number.isFinite(selectedVal) && selectedVal >= 200) {
                  Modal.warning({
                    title: 'Niedozwolona wysokość drzwi',
                    content: 'Gdy secondHeight jest mniejsze niż 200 cm, drzwi o wysokości 200 cm lub większej mogą być tylko z przodu.',
                  });
                  return;
                }

                // reguła bram na froncie (globalnie dla front wall select)
                if (wall === 'front_wall') {
                  const minGateH = getFrontGateMinHeight();
                  if (Number.isFinite(minGateH) && Number.isFinite(selectedVal) && selectedVal >= minGateH) {
                    Modal.warning({
                      title: 'Za wysokie drzwi dla przypisanych bram',
                      content: `Masz drzwi przypisane do bramy/bram o minimalnej wysokości ${minGateH} cm. Wysokość drzwi musi być mniejsza (max ${minGateH - 1} cm).`,
                    });
                    return;
                  }
                }

                dispatch(setSelectedLocal(heightKey, id));
                onDoorHeightChangeWall(wall, id);
              }}
              options={filterHeightAvailableWithGateRule(wall, heightAvailable).map((el) => ({
                id: el.id,
                value: `${el.value}`,
              }))}
              nested
            />

            <SelectEdit
              selected={openingSelected}
              loaded={loaded}
              title={openingInfo.name}
              onChange={(id) => {
                dispatch(setSelectedLocal(openingKey, id));
                onDoorOpeningChangeWall(wall, id);
              }}
              options={openingAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
              nested
            />

            {doors[wall]?.doors?.map((door, index) => {
              const isAssignedToGate = isFront && !!door.gateId;

              return (
                <React.Fragment key={door.id}>
                  {isFront && (
                    <>
                      <SelectEdit
                        selected={door.gateId ?? 0}
                        loaded={loaded}
                        title={
                          doors[wall].doors.length === 1
                            ? 'Przypisz do bramy'
                            : `Przypisz do bramy ${index + 1}`
                        }
                        onChange={(gateIdOrZero) => onDoorAssignGateChange(wall, index, gateIdOrZero)}
                        options={gatesOptions}
                        nested
                      />

                      {!!door.gateId && (
                        <SelectEdit
                          selected={door.gateSide ?? pickFreeSideForGate(door.gateId, index) ?? 'left'}
                          loaded={loaded}
                          title={
                            doors[wall].doors.length === 1
                              ? 'Strona drzwi'
                              : `Strona drzwi ${index + 1}`
                          }
                          onChange={(side) => onDoorAssignGateSideChange(wall, index, side)}
                          options={getSideOptionsForDoor(door.gateId, index, door.gateSide)}
                          nested
                        />
                      )}
                    </>
                  )}

                  {!isAssignedToGate && (
                    <RangeEdit
                      title={
                        doors[wall].doors.length === 1
                          ? t(`door_${wall.split('_')[0]}_position`)
                          : `${t(`door_${wall.split('_')[0]}_position`)} ${index + 1}`
                      }
                      min={20}
                      step={5}
                      max={max}
                      unit={'cm'}
                      selected={door.position}
                      onChange={(value) => onDoorPositionChange(wall, index, value)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </>
        )}
      </>
    );
  };

  return (
    <>
      {loaded && (
        <EditableSection
          collapsed={collapsed}
          title={t('doors')}
          nested
          subsection
          open={isDoorsOpen}
          // onOpenChange={(open) => dispatch(setUiOpenSection('doors', open))}
        >
          <Spacer>{t('door_location')}</Spacer>

          {renderWallControls(
            'back_wall',
            door_back_count_selected,
            door_back_count_available,
            door_back_count_info,
            door_back_size_selected,
            door_back_size_available,
            door_back_size_info,
            door_back_height_selected,
            door_back_height_available,
            door_back_height_info,
            door_back_opening_selected,
            door_back_opening_available,
            door_back_opening_info,
            'door_back_count',
            'door_back_size',
            'door_back_height',
            'door_back_opening',
            width - 20 - doorsWidth
          )}

          {renderWallControls(
            'front_wall',
            door_front_count_selected,
            door_front_count_available,
            door_front_count_info,
            door_front_size_selected,
            door_front_size_available,
            door_front_size_info,
            door_front_height_selected,
            door_front_height_available,
            door_front_height_info,
            door_front_opening_selected,
            door_front_opening_available,
            door_front_opening_info,
            'door_front_count',
            'door_front_size',
            'door_front_height',
            'door_front_opening',
            width - 20 - doorsWidth
          )}

          {renderWallControls(
            'left_wall',
            door_left_count_selected,
            door_left_count_available,
            door_left_count_info,
            door_left_size_selected,
            door_left_size_available,
            door_left_size_info,
            door_left_height_selected,
            door_left_height_available,
            door_left_height_info,
            door_left_opening_selected,
            door_left_opening_available,
            door_left_opening_info,
            'door_left_count',
            'door_left_size',
            'door_left_height',
            'door_left_opening',
            length - 20 - doorsWidth
          )}

          {renderWallControls(
            'right_wall',
            door_right_count_selected,
            door_right_count_available,
            door_right_count_info,
            door_right_size_selected,
            door_right_size_available,
            door_right_size_info,
            door_right_height_selected,
            door_right_height_available,
            door_right_height_info,
            door_right_opening_selected,
            door_right_opening_available,
            door_right_opening_info,
            'door_right_count',
            'door_right_size',
            'door_right_height',
            'door_right_opening',
            length - 20 - doorsWidth
          )}

          <Container>
            <ButtonWrapper>
              <Button
                icon={<InfoCircleOutlined />}
                type="primary"
                block
                onClick={openModal}
              >
                {t('more_information')}
              </Button>
            </ButtonWrapper>
          </Container>
        </EditableSection>
      )}
    </>
  );
};

DoorsEditor.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,

  door_back_count_available: PropTypes.arrayOf(availableType),
  door_back_count_info: infoShape,
  door_back_count_selected: PropTypes.number,
  door_back_size_available: PropTypes.arrayOf(availableType),
  door_back_size_info: infoShape,
  door_back_size_selected: PropTypes.number,
  door_back_height_available: PropTypes.arrayOf(availableType),
  door_back_height_info: infoShape,
  door_back_height_selected: PropTypes.number,
  door_back_opening_available: PropTypes.arrayOf(availableType),
  door_back_opening_info: infoShape,
  door_back_opening_selected: PropTypes.number,

  door_front_count_available: PropTypes.arrayOf(availableType),
  door_front_count_info: infoShape,
  door_front_count_selected: PropTypes.number,
  door_front_size_available: PropTypes.arrayOf(availableType),
  door_front_size_info: infoShape,
  door_front_size_selected: PropTypes.number,
  door_front_height_available: PropTypes.arrayOf(availableType),
  door_front_height_info: infoShape,
  door_front_height_selected: PropTypes.number,
  door_front_opening_available: PropTypes.arrayOf(availableType),
  door_front_opening_info: infoShape,
  door_front_opening_selected: PropTypes.number,

  door_left_count_available: PropTypes.arrayOf(availableType),
  door_left_count_info: infoShape,
  door_left_count_selected: PropTypes.number,
  door_left_size_available: PropTypes.arrayOf(availableType),
  door_left_size_info: infoShape,
  door_left_size_selected: PropTypes.number,
  door_left_height_available: PropTypes.arrayOf(availableType),
  door_left_height_info: infoShape,
  door_left_height_selected: PropTypes.number,
  door_left_opening_available: PropTypes.arrayOf(availableType),
  door_left_opening_info: infoShape,
  door_left_opening_selected: PropTypes.number,

  door_right_count_available: PropTypes.arrayOf(availableType),
  door_right_count_info: infoShape,
  door_right_count_selected: PropTypes.number,
  door_right_size_available: PropTypes.arrayOf(availableType),
  door_right_size_info: infoShape,
  door_right_size_selected: PropTypes.number,
  door_right_height_available: PropTypes.arrayOf(availableType),
  door_right_height_info: infoShape,
  door_right_height_selected: PropTypes.number,
  door_right_opening_available: PropTypes.arrayOf(availableType),
  door_right_opening_info: infoShape,
  door_right_opening_selected: PropTypes.number,

  loaded: PropTypes.bool
};

export default withTranslation(DoorsEditor);
